// ── Novelty Filter Bar ─────────────────────────────────────
// Floating chip bar above the canvas that lets the user filter
// the tree by novelty score and QD cell coordinates.
// Auto-hides when the engine hasn't produced results for this run.

import React from 'react';
import { useNovelty, noveltyColor } from './NoveltyContext';

const MIN_NOVELTY_PRESETS = [
  { label: 'all', value: 0 },
  { label: '40+ variant', value: 40 },
  { label: '70+ notable', value: 70 },
];

export default function NoveltyFilterBar() {
  const { scores, archive, filters, setFilter, clearFilters } = useNovelty();
  const scoredCount = Object.keys(scores).length;
  if (scoredCount === 0) return null; // nothing to filter yet

  const dimensions = archive?.dimensions || {};
  const activeFilterCount = (filters.minNovelty > 0 ? 1 : 0)
    + (filters.cellMechanism ? 1 : 0)
    + (filters.cellRisk ? 1 : 0)
    + (filters.cellAudience ? 1 : 0)
    + (filters.cellHorizon ? 1 : 0);

  const chipBase = {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    padding: '4px 10px',
    borderRadius: 999,
    background: '#181818',
    border: '1px solid #2a2a2a',
    color: '#aaa',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };
  const activeChip = {
    background: '#2a1f4a',
    border: '1px solid #6c63ff',
    color: '#fff',
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        zIndex: 50,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        alignItems: 'center',
        pointerEvents: 'none', // let the bar's contents catch clicks but not the gaps
      }}
    >
      {/* "Filter:" label */}
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#666', pointerEvents: 'auto' }}>
        🧬 filter ·
      </span>

      {/* Min novelty presets */}
      {MIN_NOVELTY_PRESETS.map(p => (
        <button
          key={p.value}
          onClick={() => setFilter('minNovelty', filters.minNovelty === p.value ? 0 : p.value)}
          style={{
            ...chipBase,
            ...(filters.minNovelty === p.value ? activeChip : {}),
            pointerEvents: 'auto',
          }}
        >
          {p.value > 0 && (
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: noveltyColor(p.value), marginRight: 4, verticalAlign: 'middle' }} />
          )}
          {p.label}
        </button>
      ))}

      {/* Divider */}
      {Object.keys(dimensions).length > 0 && (
        <span style={{ color: '#333', pointerEvents: 'auto' }}>·</span>
      )}

      {/* Cell-coordinate filters */}
      {Object.entries(dimensions).map(([dim, bins]) => {
        const filterKey = 'cell' + dim.charAt(0).toUpperCase() + dim.slice(1);
        const current = filters[filterKey];
        return (
          <CellDropdown
            key={dim}
            label={dim}
            bins={bins}
            value={current}
            onChange={v => setFilter(filterKey, v)}
            chipBase={chipBase}
            activeChip={activeChip}
          />
        );
      })}

      {/* Clear */}
      {activeFilterCount > 0 && (
        <button
          onClick={clearFilters}
          style={{
            ...chipBase,
            color: '#f87171',
            border: '1px solid #4a1f1f',
            pointerEvents: 'auto',
          }}
          title="Clear all novelty filters"
        >
          ✕ clear ({activeFilterCount})
        </button>
      )}
    </div>
  );
}

function CellDropdown({ label, bins, value, onChange, chipBase, activeChip }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', pointerEvents: 'auto' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          ...chipBase,
          ...(value ? activeChip : {}),
        }}
      >
        {label}{value ? `: ${value}` : ''} ▾
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            background: '#181818',
            border: '1px solid #2a2a2a',
            borderRadius: 6,
            padding: 4,
            minWidth: 140,
            zIndex: 100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <button
            onClick={() => { onChange(null); setOpen(false); }}
            style={{
              background: 'none',
              border: 'none',
              color: !value ? '#fff' : '#888',
              padding: '4px 8px',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              textAlign: 'left',
              cursor: 'pointer',
              borderRadius: 4,
            }}
          >
            (any)
          </button>
          {bins.map(b => (
            <button
              key={b}
              onClick={() => { onChange(b); setOpen(false); }}
              style={{
                background: value === b ? '#2a1f4a' : 'none',
                border: 'none',
                color: value === b ? '#fff' : '#bbb',
                padding: '4px 8px',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: 4,
              }}
              onMouseEnter={e => { if (value !== b) e.target.style.background = '#222'; }}
              onMouseLeave={e => { if (value !== b) e.target.style.background = 'none'; }}
            >
              {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
